import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Users } from "lucide-react";
import { api } from "../lib/api";

// 2026-10-27 — broker-truth P&L per Kite account (kite_ledger.py): real fills,
// Autopilot vs copied vs Manual (StoxTrade) vs Manual (Kite app). Admin can pick
// any account; everyone else sees only their own. Used on the Stocks Reports page
// (market="stocks") and Commodities P&L (market="commodities").

const rs = (v) => (v == null || Number.isNaN(Number(v)) ? "—"
  : `${v >= 0 ? "+" : "−"}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const col = (v) => (v == null ? undefined : v > 0 ? "var(--bull)" : v < 0 ? "var(--bear)" : undefined);
const GROUP_COLORS = { auto: "var(--bull)", copy: "#5dade2", manual_app: "#f5b041", manual_kite: "#af7ac5" };

const Bucket = ({ title, b }) => (
  <div className="border border-grid p-3 font-mono-data text-[11px] space-y-1">
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3">{title}</div>
    <div className="text-xl font-semibold" style={{ color: col(b?.net_rs) }}>{rs(b?.net_rs)}</div>
    <div className="text-muted-2">
      {b?.trades ?? 0} trades · {b?.win_rate_pct ?? "—"}% win · PF {b?.profit_factor ?? "—"}
    </div>
    <div className="text-muted-3">gross {rs(b?.gross_rs)} · charges −₹{Math.round(b?.charges_rs || 0).toLocaleString("en-IN")}</div>
  </div>
);

const Split = ({ period }) => {
  const groups = period?.by_group || [];
  if (!groups.length) return <div className="text-[11px] text-muted-3 font-mono-data">No closed trades.</div>;
  return (
    <div className="space-y-1">
      {groups.map((g) => (
        <div key={g.group} className="flex justify-between text-[11px] font-mono-data">
          <span><span className="inline-block w-2 h-2 mr-2 rounded-full" style={{ background: GROUP_COLORS[g.group] }} />{g.label}</span>
          <span style={{ color: col(g.net_rs) }}>{g.trades}t · {rs(g.net_rs)}</span>
        </div>
      ))}
      {(period.by_reason || []).length > 0 && (
        <div className="pt-1 mt-1 border-t border-grid/60" data-testid="pnl-by-reason">
          <div className="text-[9px] uppercase tracking-[0.15em] text-muted-3">Manual trades by reason</div>
          {period.by_reason.map((r) => (
            <div key={r.reason} className="flex justify-between text-[10px] font-mono-data text-muted-2">
              <span>{r.reason}</span><span style={{ color: col(r.net_rs) }}>{r.trades}t · {r.win_rate_pct ?? "—"}% · {rs(r.net_rs)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="pt-1 mt-1 border-t border-grid/60">
        {(period.by_source || []).slice(0, 8).map((s) => (
          <div key={s.source} className="flex justify-between text-[10px] font-mono-data text-muted-2">
            <span>{s.label}</span><span style={{ color: col(s.net_rs) }}>{s.trades}t · {rs(s.net_rs)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AccountPnL = ({ market = "all", title = "Account P&L · real fills (auto + manual)" }) => {
  const [account, setAccount] = useState("all");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });

  const load = useCallback(async () => {
    try {
      const params = { account, market };
      if (range.from && range.to) Object.assign(params, { from_date: range.from, to_date: range.to });
      const { data: d } = await api.get("/pnl/ledger", { params });
      setData(d);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Couldn't load P&L" });
    }
  }, [account, market, range]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try { await api.post("/pnl/ledger/refresh"); await load(); toast.success("Fills refreshed from Kite"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  if (data.error && !(data.accounts_available || []).length) {
    return (
      <div className="border border-grid p-3 text-xs font-mono-data" style={{ color: "var(--bear)" }} data-testid={`account-pnl-${market}`}>
        Account P&L (real fills): {data.error}
      </div>
    );
  }
  if (!(data.accounts_available || []).length) return null;   // no Kite account for this user
  const p = data.periods || {};
  const many = (data.accounts_available || []).length > 1;

  return (
    <div className="border border-grid p-4 space-y-4" data-testid={`account-pnl-${market}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2"><Users size={12} /> {title}</div>
        <div className="flex flex-wrap items-center gap-2 font-mono-data text-[11px]">
          {many && (
            <select value={account} onChange={(e) => setAccount(e.target.value)} className="bg-transparent border border-grid px-2 py-1"
                    data-testid="account-pnl-select">
              <option value="all">All accounts</option>
              {data.accounts_available.map((a) => <option key={a.account_id} value={a.account_id}>{a.label}</option>)}
            </select>
          )}
          <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                 className="bg-transparent border border-grid px-2 py-1" />
          <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                 className="bg-transparent border border-grid px-2 py-1" />
          {(range.from || range.to) && <button className="px-2 py-1 border border-grid" onClick={() => setRange({ from: "", to: "" })}>clear</button>}
          <button className="px-2 py-1 border border-grid inline-flex items-center gap-1" onClick={refresh} disabled={busy}>
            <RefreshCw size={11} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {data.error ? <div className="text-xs font-mono-data" style={{ color: "var(--bear)" }}>{data.error}</div> : (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-2"><Bucket title={`This week · from ${p.week?.from}`} b={p.week?.overall} /><Split period={p.week} /></div>
            <div className="space-y-2"><Bucket title={`This month · from ${p.month?.from}`} b={p.month?.overall} /><Split period={p.month} /></div>
            {p.custom ? <div className="space-y-2"><Bucket title={`${p.custom.from} → ${p.custom.to}`} b={p.custom.overall} /><Split period={p.custom} /></div>
              : <div className="text-[10px] text-muted-3 font-mono-data self-center">Pick From and To dates for a custom range.</div>}
          </div>

          {account === "all" && (data.month_by_account || []).length > 1 && (
            <div className="flex flex-wrap gap-4 text-[11px] font-mono-data">
              {data.month_by_account.map((a) => (
                <span key={a.account}>{a.label}: <span style={{ color: col(a.net_rs) }}>{rs(a.net_rs)}</span> <span className="text-muted-3">({a.trades}t this month)</span></span>
              ))}
            </div>
          )}

          {(data.open_positions || []).length > 0 && (
            <div className="text-[11px] font-mono-data">
              <div className="text-[9px] uppercase tracking-widest text-muted-3 mb-1">Open (not yet in P&L)</div>
              {data.open_positions.map((o) => (
                <div key={`${o.account}${o.tradingsymbol}`} className="text-muted-2">
                  {many ? `${o.account_label} · ` : ""}{o.tradingsymbol} · {o.qty > 0 ? "long" : "short"} {Math.abs(o.qty)} @ {o.avg_price} · {o.source_label} · since {o.since}
                </div>
              ))}
            </div>
          )}

          <details className="border border-grid/60">
            <summary className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-mono-data text-muted-2 cursor-pointer">
              Closed trades ({(data.trades || []).length})
            </summary>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono-data">
                <thead><tr className="text-left text-muted-3 text-[9px] uppercase tracking-widest border-b border-grid">
                  {["Closed", many ? "Account" : null, "Contract", "Source", "Qty", "Entry", "Exit", "Net ₹"].filter(Boolean).map((h) => <th key={h} className="px-2 py-1.5 font-normal">{h}</th>)}
                </tr></thead>
                <tbody>
                  {(data.trades || []).map((t, i) => (
                    <tr key={i} className="border-b border-grid/30">
                      <td className="px-2 py-1 text-muted-3">{(t.exit_ts || "").slice(0, 16)}</td>
                      {many && <td className="px-2 py-1">{t.account_label}</td>}
                      <td className="px-2 py-1">{t.tradingsymbol}{t.direction === "SHORT" ? <span className="text-muted-3"> (short)</span> : null}</td>
                      <td className="px-2 py-1">{t.source_label}{t.exit_manual ? <span className="text-muted-3"> · exited manually</span> : null}</td>
                      <td className="px-2 py-1">{t.qty}</td>
                      <td className="px-2 py-1">{t.estimated ? "≈" : ""}{t.entry_price}</td>
                      <td className="px-2 py-1">{t.estimated ? "≈" : ""}{t.exit_price}</td>
                      <td className="px-2 py-1" style={{ color: col(t.net_rs) }}>{rs(t.net_rs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          <div className="text-[10px] text-muted-3 font-mono-data">
            From real Kite fills{data.ledger_since ? ` since ${data.ledger_since}` : " (capture starts after this deploy)"} — includes manual orders from this app and the Kite app.
            {data.mirror_backfilled ? " Copied trades before that are rebuilt from the copy log." : ""}
            {" "}Manual app orders from before then are estimated at their last limit price (≈). Charges are estimates. A position counts once it is closed.
          </div>
        </>
      )}
    </div>
  );
};

export default AccountPnL;
